// FINANCIALS — Fieldlink's Commercial Command Centre.
// One authoritative data source: GET /api/financials/dashboard.
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Settings, Download, Loader2, CalendarRange, RefreshCw } from "lucide-react";
import {
  KpiRow, TrendChart, CommercialActions, CashPosition, AgeingCard, JobProfitability,
  KiriBrief, RecentInvoices, RecentVariations, Skeleton, useNav, CARD,
} from "@/components/financials/FinancialsParts";

const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/public/quote-hero-default.jpg`;

const PERIODS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_quarter", label: "Quarter" },
  { value: "this_year", label: "YTD" },
  { value: "custom", label: "Custom" },
];

export default function Financials() {
  const { user } = useAuth();
  const nav = useNav();
  const [period, setPeriod] = useState("this_month");
  const [range, setRange] = useState({ from: "", to: "" });
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    if (period === "custom" && !(range.from && range.to)) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/financials/dashboard", {
        params: { period, trend_months: months, from_date: range.from || undefined, to_date: range.to || undefined },
      });
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load financials");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period, months, range.from, range.to]);

  const exportCsv = async (type) => {
    try {
      const res = await api.get("/financials/export", {
        params: { type, period, from_date: range.from || undefined, to_date: range.to || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "job_profitability" ? "job_profitability.csv" : "financials_summary.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const jobRows = useMemo(() => {
    let rows = data?.job_profitability || [];
    if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => (r.title || "").toLowerCase().includes(q)
        || (r.job_number || "").toLowerCase().includes(q)
        || (r.client_name || "").toLowerCase().includes(q));
    }
    return [...rows].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0));
  }, [data, search, statusFilter]);

  if (user.role !== "admin" && user.role !== "super_admin") {
    return <Navigate to="/app" replace />;
  }

  const target = data?.financial_settings?.target_margin ?? 20;

  return (
    <div className="min-h-screen bg-[#0B0F14] text-zinc-100 overflow-x-hidden" data-testid="financials-page">
      {/* ---------- hero ---------- */}
      <header className="relative overflow-hidden border-b border-[#1E2733]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden />
        <div className="absolute inset-0" aria-hidden
          style={{ background: "linear-gradient(95deg,#0B0F14 0%,rgba(11,15,20,0.94) 38%,rgba(11,15,20,0.55) 100%)" }} />
        <div className="relative px-4 sm:px-6 pt-5 pb-4">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.24em] text-[#FF5F15]">// Financials</div>
              <h1 className="mt-1.5 text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none text-white">Financials</h1>
              <p className="mt-2.5 text-[13px] text-zinc-400 max-w-2xl leading-relaxed">
                Your commercial command centre — live revenue, cost, margin and WIP calculated straight from jobs, invoices, timesheets and materials.
              </p>
            </div>
            <div className="hidden md:block text-right shrink-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Good jobs</div>
              <div className="text-[13px] font-black uppercase tracking-[0.16em] text-white">Great businesses</div>
              <div className="h-[3px] w-24 ml-auto mt-2" style={{ background: "#FF5F15" }} />
            </div>
          </div>

          {/* period selector + tools */}
          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap max-w-full">
            <div className="flex items-center gap-1 p-1 rounded-md bg-[#0E141C]/90 border border-[#25313E] max-w-full overflow-x-auto no-scrollbar" data-testid="period-selector">
              {PERIODS.map((p) => (
                <button key={p.value} onClick={() => setPeriod(p.value)}
                  className={`px-2.5 sm:px-3.5 py-2 rounded-sm text-[10px] sm:text-[11px] font-black uppercase tracking-[0.1em] transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
                    period === p.value ? "bg-[#FF5F15] text-white" : "text-zinc-400 hover:text-white"}`}
                  data-testid={`period-${p.value}`}>
                  {p.label}
                  {p.value === "custom" && <CalendarRange className="w-3.5 h-3.5" />}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {period === "custom" && (
                <div className="flex items-center gap-2" data-testid="custom-range">
                  <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                    className="bg-[#0E141C] border border-[#25313E] text-zinc-200 text-xs rounded-sm px-2 py-2 outline-none focus:border-[#FF5F15]"
                    data-testid="range-from" />
                  <span className="text-zinc-600 text-xs">to</span>
                  <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                    className="bg-[#0E141C] border border-[#25313E] text-zinc-200 text-xs rounded-sm px-2 py-2 outline-none focus:border-[#FF5F15]"
                    data-testid="range-to" />
                </div>
              )}
              <button onClick={() => exportCsv("summary")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#0E141C] border border-[#25313E] text-[10px] font-black uppercase tracking-[0.12em] text-zinc-200 hover:border-[#FF5F15] transition-colors"
                data-testid="financials-export">
                <Download className="w-3.5 h-3.5" /> Export
              </button>
              <button onClick={() => setSettingsOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#0E141C] border border-[#25313E] text-[10px] font-black uppercase tracking-[0.12em] text-zinc-200 hover:border-[#FF5F15] transition-colors"
                data-testid="financials-settings-btn">
                <Settings className="w-3.5 h-3.5" /> Settings
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-6 py-4 space-y-3">
        {error && (
          <div className={`${CARD} px-4 py-6 flex items-center justify-between gap-4 flex-wrap`} data-testid="financials-error">
            <div>
              <div className="text-sm font-black uppercase tracking-wide text-red-400">Financials couldn&apos;t load</div>
              <p className="text-xs text-zinc-400 mt-1">{error}</p>
            </div>
            <Button onClick={load} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold uppercase tracking-wider" data-testid="financials-retry">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
            </Button>
          </div>
        )}

        {loading && !data && <Skeleton />}

        {data && (
          <>
            <KpiRow k={data.kpis} nav={nav} />

            <div className="grid xl:grid-cols-12 gap-3">
              <div className="xl:col-span-8"><TrendChart trend={data.trend} months={months} onMonthsChange={setMonths} /></div>
              <div className="xl:col-span-4"><CommercialActions actions={data.commercial_actions} nav={nav} /></div>
            </div>

            <div className="grid xl:grid-cols-12 gap-3">
              <div className="xl:col-span-7"><CashPosition cash={data.cash_position} /></div>
              <div className="xl:col-span-5"><AgeingCard ageing={data.ageing} nav={nav} /></div>
            </div>

            <div id="job-profitability">
              <JobProfitability rows={jobRows} total={(data.job_profitability || []).length} target={target}
                search={search} onSearch={setSearch} status={statusFilter} onStatus={setStatusFilter}
                onExport={() => exportCsv("job_profitability")} nav={nav} />
            </div>

            <div className="grid xl:grid-cols-12 gap-3">
              <div className="xl:col-span-4"><KiriBrief brief={data.kiri_brief} /></div>
              <div className="xl:col-span-4"><RecentInvoices invoices={data.recent_invoices} nav={nav} /></div>
              <div className="xl:col-span-4"><RecentVariations variations={data.recent_variations} nav={nav} /></div>
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-xs text-zinc-500 py-1" data-testid="financials-refreshing">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating…
              </div>
            )}
          </>
        )}
      </div>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={data?.financial_settings}
        onSaved={(fs) => { setData((d) => ({ ...d, financial_settings: fs })); load(); }} />
    </div>
  );
}

function SettingsDialog({ open, onClose, settings, onSaved }) {
  const [form, setForm] = useState({ target_margin: 20, low_margin_warning: 10, over_budget_warning: 100 });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && settings) setForm(settings); }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/org/me", {
        financial_settings: {
          target_margin: Number(form.target_margin),
          low_margin_warning: Number(form.low_margin_warning),
          over_budget_warning: Number(form.over_budget_warning),
        },
      });
      toast.success("Financial settings saved");
      onSaved?.(data.financial_settings);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl max-w-md" data-testid="financial-settings-dialog">
        <DialogHeader>
          <DialogTitle>Financial settings</DialogTitle>
          <DialogDescription>Thresholds that drive job margin health, Commercial Actions and Kiri&apos;s brief.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {[["target_margin", "Target gross margin (%)", "Used for job margin health bars and margin risk."],
            ["low_margin_warning", "Low margin warning (%)", "Jobs below this margin are flagged for review."],
            ["over_budget_warning", "Over budget warning (%)", "e.g. 100% = flag when actual cost reaches budget."]].map(([key, label, help]) => (
            <div key={key}>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</Label>
              <Input type="number" min="0" value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="mt-1.5 rounded-sm border-zinc-400" data-testid={`settings-${key.replace(/_/g, "-")}-input`} />
              <p className="text-[11px] text-zinc-500 mt-1">{help}</p>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="settings-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
